// Single source of truth for the course hierarchy
// (Course -> Module -> Chapter -> Unit -> CourseLesson), plus Topic as a
// separate, orthogonal concept. Topic is NOT part of the childKey chain -
// it used to be lesson's structural child, but the backend now lets a
// Topic attach directly under any of course/module/chapter/unit/lesson
// (exactly one of Topic's 5 parent fields is set, the backend 400s
// otherwise). Every level below marks itself `canHostTopics: true`
// accordingly. Because each hostable level's own `key` is identical to
// Topic's corresponding parent-field name (course/module/chapter/unit/
// lesson), `level.key` alone doubles as both the `?filter=` query param
// and the payload field name when creating/fetching topics under it - no
// separate mapping table needed.
//
// Two separate child-pointer fields are deliberately kept apart:
//   - childKey drives OutlineNode's tree recursion for STRUCTURE (single
//     next level, e.g. module -> chapter). Lesson has none - CourseLesson
//     is the deepest structural level.
//   - childChecks drives the pre-delete "does this still have children"
//     guard (courseLevels backend has no cascade delete at all - see
//     api.js's hasChildren). Every hostable level's own topics collection
//     (`/api/topics?<level.key>=`) is always one of its childChecks,
//     alongside its structural childKey's collection when it has one.

export const LEVELS = [
    {
        key: 'course', label: 'Курс', apiBase: '/api/courses',
        parentField: null, filterParam: null,
        childKey: 'module', canHostTopics: true,
        childChecks: [
            { apiBase: '/api/modules', filterParam: 'course' },
            { apiBase: '/api/topics', filterParam: 'course' },
        ],
    },
    {
        key: 'module', label: 'Модуль', apiBase: '/api/modules',
        parentField: 'course', filterParam: 'course',
        childKey: 'chapter', canHostTopics: true,
        childChecks: [
            { apiBase: '/api/chapters', filterParam: 'module' },
            { apiBase: '/api/topics', filterParam: 'module' },
        ],
    },
    {
        key: 'chapter', label: 'Розділ', apiBase: '/api/chapters',
        parentField: 'module', filterParam: 'module',
        childKey: 'unit', canHostTopics: true,
        childChecks: [
            { apiBase: '/api/units', filterParam: 'chapter' },
            { apiBase: '/api/topics', filterParam: 'chapter' },
        ],
    },
    {
        key: 'unit', label: 'Юніт', apiBase: '/api/units',
        parentField: 'chapter', filterParam: 'chapter',
        childKey: 'lesson', canHostTopics: true,
        childChecks: [
            { apiBase: '/api/courselessons', filterParam: 'unit' },
            { apiBase: '/api/topics', filterParam: 'unit' },
        ],
    },
    {
        key: 'lesson', label: 'Урок', apiBase: '/api/courselessons',
        parentField: 'unit', filterParam: 'unit',
        childKey: null, canHostTopics: true,
        childChecks: [
            { apiBase: '/api/topics', filterParam: 'lesson' },
        ],
    },
    {
        key: 'topic', label: 'Тема', apiBase: '/api/topics',
        // No single fixed parentField anymore - which of the 5 possible
        // parent fields applies depends on which level hosts a given
        // Topic instance (see coursesPayload.js's buildTopicPayload).
        parentField: null, filterParam: null,
        childKey: null, canHostTopics: false,
        childChecks: [
            { apiBase: '/api/topicelements', filterParam: 'topic' },
            { apiBase: '/api/topicexercisetemplates', filterParam: 'topic' },
            { apiBase: '/api/topiccontainers', filterParam: 'topic' },
        ],
    },
];

export const LEVELS_BY_KEY = LEVELS.reduce((acc, level) => {
    acc[level.key] = level;
    return acc;
}, {});

// The 5 levels a Topic can be directly attached to, in hierarchy order.
export const TOPIC_HOST_LEVEL_KEYS = ['course', 'module', 'chapter', 'unit', 'lesson'];

export function nextLevel(levelKey) {
    const level = LEVELS_BY_KEY[levelKey];
    return level && level.childKey ? LEVELS_BY_KEY[level.childKey] : null;
}

// Given a Topic object (as returned by any GET that embeds it - every
// relation comes back fully nested on read, confirmed throughout this
// backend), walk up whichever of its 5 parent fields is actually set until
// reaching the Course itself, and return that Course's id. Used by
// LessonTopicFlyout.jsx's pills to link straight into
// /students/:id/workspace/:courseId - a Topic doesn't carry its own
// courseId directly unless it's attached at the course level itself.
export function resolveTopicCourseId(topic) {
    for (const key of TOPIC_HOST_LEVEL_KEYS) {
        let node = topic[key];
        if (!node) continue;
        let levelKey = key;
        while (levelKey !== 'course') {
            const level = LEVELS_BY_KEY[levelKey];
            node = node[level.parentField];
            levelKey = level.parentField;
            if (!node) return null;
        }
        return node.id;
    }
    return null;
}
